/**
 * True for same-origin app routes (works with HashRouter and BrowserRouter).
 * Excludes in-page anchors (#section), protocol-relative URLs, and "/#..." mashups.
 */
export function isInternalAppPath(href) {
    if (href == null || href === '' || href === '#')
        return false;
    if (href.startsWith('#'))
        return false;
    if (!href.startsWith('/'))
        return false;
    if (href.startsWith('//'))
        return false;
    if (href.startsWith('/#'))
        return false;
    return true;
}
export function parseHashRouterNavIntent(href) {
    const h = href.trim();
    if (!h)
        return { kind: 'document', href: '#' };
    if (/^https?:\/\//i.test(h) || h.startsWith('mailto:'))
        return { kind: 'document', href: h };
    /**
     * HashRouter keeps the app path in the hash as `#/…` (e.g. `#/permits/env`). Treat `#/foo` like `/foo`
     * so we return `app` / `home` intents and `navigate()` — not `window.location.assign('#/…')`, which can
     * leave React Router’s location out of sync and strand users on a permit view with empty params.
     */
    if (h.startsWith('#/')) {
        return parseHashRouterNavIntent(h.slice(1));
    }
    if (h.startsWith('#') && !h.startsWith('#/')) {
        const id = h.slice(1).split('?')[0];
        return id ? { kind: 'home', scrollToId: id } : { kind: 'home' };
    }
    if (h.startsWith('/#') && h.length > 2) {
        const id = h.slice(2).split('?')[0].replace(/^\//, '');
        return id ? { kind: 'home', scrollToId: id } : { kind: 'home' };
    }
    if (h === '/' || h === '/#')
        return { kind: 'home' };
    if (h.startsWith('/') && !h.startsWith('//')) {
        const { pathname, search } = splitPathAndSearch(h);
        if (isInternalAppPath(pathname)) {
            return { kind: 'app', pathname, search: search || undefined };
        }
    }
    return { kind: 'document', href: h };
}
/** After `navigate('/')`, landing sections may not be mounted yet — retry until the target exists. */
export function scheduleScrollToElementAfterRoute(elementId, maxAttempts = 56) {
    let attempts = 0;
    const tick = () => {
        const el = document.getElementById(elementId);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        attempts += 1;
        if (attempts >= maxAttempts)
            return;
        window.requestAnimationFrame(tick);
    };
    window.setTimeout(tick, 0);
}
export function splitPathAndSearch(href) {
    const i = href.indexOf('?');
    if (i === -1)
        return { pathname: href, search: '' };
    return { pathname: href.slice(0, i), search: href.slice(i) };
}
/** Client-side route for internal paths; full navigation for external URLs. */
export function navigateOrAssign(navigate, href) {
    const { pathname, search } = splitPathAndSearch(href);
    if (isInternalAppPath(pathname)) {
        navigate({ pathname, search: search || undefined });
    }
    else {
        window.location.href = href;
    }
}
